import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Detect plan-quota error messages emitted by the backend so we can surface a
// shared "Mejorar plan" CTA toast across the entire app from a single place.
const PLAN_LIMIT_RX = /(plan\s+\w+\s+permite|máximo|maximo|límite|limite).+(restaurant|menú|menu|usuario|equipo|miembro|plato|categor)/i;
const PLAN_LIMIT_FALLBACK_RX = /mejora\s+tu\s+plan/i;

// Detect AI-credit / IA-budget exhaustion errors (status 422 or 429). These
// deserve their own dual-CTA toast: "Ampliar plan" (long-term) + "Comprar
// créditos" (short-term). Recharge link points to the credits tab of the
// subscription page.
const AI_CREDITS_RX = /(creditos?|créditos?|presupuesto\s+de\s+ia|ia\s+agotado|saldo\s+ia)/i;

// Cross-tab throttle: keep the last-shown timestamp in localStorage so two
// open tabs of the app don't both fire the same paywall toast simultaneously.
const TOAST_THROTTLE_MS = 2500;
const _LS_PLAN_KEY = "_mg_plan_toast_ts";
const _LS_AI_KEY = "_mg_ai_toast_ts";

function _shouldShowToast(lsKey) {
  try {
    const last = Number(localStorage.getItem(lsKey) || 0);
    if (Date.now() - last < TOAST_THROTTLE_MS) return false;
    localStorage.setItem(lsKey, String(Date.now()));
    return true;
  } catch {
    // localStorage may be unavailable (private mode, embedded iframe). Fall
    // back to letting the toast fire — duplicate toasts are recoverable.
    return true;
  }
}

// Fire-and-forget POST to /api/events/client-toast so we get analytics
// of which endpoints choke users with quotas. Never throws.
// EXPORTED: also reused by frontend components (e.g. StarterUsageBanner).
export function logClientEvent({ type, status = 200, endpoint = "", message = null, restaurant_id = null }) {
  try {
    const url = `${API_URL}/api/events/client-toast`;
    const headers = { "Content-Type": "application/json" };
    if (api && api._token) headers["Authorization"] = `Bearer ${api._token}`;
    const body = JSON.stringify({
      type,
      status,
      endpoint: String(endpoint).slice(0, 300),
      message: message ? String(message).slice(0, 600) : null,
      restaurant_id,
    });
    fetch(url, { method: "POST", headers, credentials: "include", body, keepalive: true }).catch(() => {});
  } catch { /* swallow */ }
}

function _logToastEvent({ type, status, endpoint, message }) {
  // Internal wrapper that auto-extracts restaurant_id from the endpoint URL.
  const m = String(endpoint || "").match(/\/restaurants\/([^/?]+)/);
  logClientEvent({ type, status, endpoint, message, restaurant_id: m ? m[1] : null });
}

function _showPlanLimitToast(message, endpointForLog) {
  if (!_shouldShowToast(_LS_PLAN_KEY)) return;
  toast.error("Has alcanzado el límite de tu plan", {
    description: message,
    duration: 12000,
    action: {
      label: "Mejorar plan",
      onClick: () => { window.location.href = "/app/subscription?tab=plans"; },
    },
  });
  _logToastEvent({ type: "plan_limit", status: 403, endpoint: endpointForLog || "", message });
}

function _showAiCreditsToast(message, status, endpointForLog) {
  if (!_shouldShowToast(_LS_AI_KEY)) return;
  // 429 means throttling (transient), 422 typically means out of credits (action required).
  const title = status === 429 ? "Análisis IA bloqueado temporalmente" : "Créditos IA insuficientes";
  toast.error(title, {
    description: message || "Has alcanzado el límite de análisis IA. Actualiza tu plan o compra más créditos para seguir.",
    duration: 12000,
    action: {
      label: "Ampliar plan",
      onClick: () => { window.location.href = "/app/subscription?tab=plans"; },
    },
    cancel: {
      label: "Comprar créditos",
      onClick: () => { window.location.href = "/app/subscription?tab=credits"; },
    },
  });
  _logToastEvent({ type: "ai_credits", status, endpoint: endpointForLog || "", message });
}

class ApiClient {
  constructor() {
    this.baseUrl = `${API_URL}/api`;
    this._token = null; // In-memory token fallback for browsers that block cookies
  }

  setToken(token) {
    this._token = token;
  }

  clearToken() {
    this._token = null;
  }

  getHeaders() {
    const headers = {
      "Content-Type": "application/json",
    };
    // Attach Bearer token as fallback for mobile browsers that block httpOnly cookies
    if (this._token) {
      headers["Authorization"] = `Bearer ${this._token}`;
    }
    return headers;
  }

  // For multipart/form-data uploads: the browser MUST set Content-Type itself so
  // it can include the boundary. Just return the Authorization header.
  getAuthOnlyHeaders() {
    const headers = {};
    if (this._token) headers["Authorization"] = `Bearer ${this._token}`;
    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
      credentials: "include",
    };

    try {
      const response = await fetch(url, config);
      
      if (response.status === 401) {
        // Don't redirect if we're already on an auth endpoint or checking auth status
        const isAuthEndpoint = endpoint.startsWith("/auth/");
        if (!isAuthEndpoint) {
          this.clearToken();
          window.location.href = "/login";
        }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || "Unauthorized");
      }

      if (!response.ok) {
        let detail = "";
        let errorBody = null;
        try {
          const cloned = response.clone();
          errorBody = await cloned.json();
          detail = errorBody.detail || errorBody.message || "";
        } catch {
          try {
            detail = await response.text();
          } catch { /* empty */ }
        }
        // If detail is an object (structured error like {code, message, ...}),
        // use its message for the Error text and attach the payload on the error
        const detailText = (detail && typeof detail === "object")
          ? (detail.message || detail.code || JSON.stringify(detail))
          : detail;
        const err = new Error(detailText || `Error del servidor (${response.status})`);
        err.status = response.status;
        err.detail = detail;
        err.body = errorBody;
        // Auto-handle plan-quota errors with a single shared "Mejorar plan" toast.
        // Components can check `err.isPlanLimit` to skip their own fallback toast.
        if (response.status === 403 && typeof detailText === "string"
            && (PLAN_LIMIT_RX.test(detailText) || PLAN_LIMIT_FALLBACK_RX.test(detailText))) {
          err.isPlanLimit = true;
          _showPlanLimitToast(detailText, endpoint);
        }
        // Auto-handle AI-credit exhaustion (422 / 429) with a dual-CTA toast.
        if ((response.status === 422 || response.status === 429)
            && typeof detailText === "string" && AI_CREDITS_RX.test(detailText)) {
          err.isAiCreditsLimit = true;
          _showAiCreditsToast(detailText, response.status, endpoint);
        }
        // Auto-detect a stale currentRestaurant. When the backend says the user
        // doesn't have access OR the restaurant is gone, we emit a global event
        // so RestaurantContext can refresh + fallback transparently.
        if (response.status === 403 || response.status === 404) {
          const m = String(endpoint || "").match(/\/restaurants\/([^/?]+)/);
          const looksLikeAccessLost = response.status === 404
            || /no tienes acceso|restaurante|restaurant/i.test(String(detailText || ""));
          if (m && looksLikeAccessLost && typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("restaurant-access-lost", {
              detail: { restaurant_id: m[1], status: response.status, endpoint, message: String(detailText || "") },
            }));
          }
        }
        throw err;
      }

      return response.json().then(json => {
        // Notify listeners when an endpoint that consumed credits returns
        // (so global UI can refresh the credit balance & milestone toasts).
        try {
          const ep = String(endpoint || "");
          const consumed = /\/ai\/|\/images?\/generate|\/recipes?\/generate|\/menu_imports|\/finance\/insights/.test(ep);
          if (consumed && typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("credits-changed", { detail: { endpoint: ep } }));
          }
        } catch {/* noop */}
        return json;
      });
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // Generic HTTP methods for flexible API calls
  async get(endpoint) {
    return this.request(endpoint, { method: "GET" });
  }

  async downloadFile(endpoint, filename) {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      headers: this.getHeaders(),
      credentials: "include",
    });
    if (!response.ok) {
      const err = new Error(`Error al descargar (${response.status})`);
      err.status = response.status;
      throw err;
    }
    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: "DELETE" });
  }

  // Auth
  async login(email, password) {
    const data = await this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    // Store token in memory for browsers that block httpOnly cookies (mobile Safari, etc.)
    if (data.access_token) {
      this.setToken(data.access_token);
    }
    return data;
  }

  async register(name, email, password) {
    const data = await this.request("/auth/register", {
      method: "POST",
      body: JSON.stringify({ name, email, password }),
    });
    if (data.access_token) {
      this.setToken(data.access_token);
    }
    return data;
  }

  async googleAuthSession(sessionId) {
    // The /auth/session call is the hot spot for browser-extension fetch hooks
    // (Redux/React DevTools, privacy extensions, monitoring tools, even
    // analytics SDKs like PostHog) that proxy requests through `postMessage`
    // to a content script — Request objects are NOT structured-cloneable so
    // the call may reject with one of several distinct errors depending on
    // the extension's implementation:
    //   - "Request object could not be cloned"  (Chrome strict)
    //   - "Failed to fetch"                     (generic TypeError when the
    //                                            extension's wrapper throws)
    //   - "Network request failed"              (Safari/iOS variant)
    //   - "Load failed"                         (Safari/iOS another variant)
    // Use XHR as a fallback for any of these — extensions hook fetch() and
    // XMLHttpRequest through different paths so XHR usually works.
    const url = `${this.baseUrl}/auth/session`;
    const payload = JSON.stringify({ session_id: sessionId });

    // Diagnostic telemetry (Feb 2026 iter.209) — captures the actual URL
    // being called + UA so if the bug recurs we can ask the user for the
    // console output via remote-debug instead of guessing.
    const diag = {
      url,
      origin: typeof window !== "undefined" ? window.location.origin : "unknown",
      ua: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      ts: new Date().toISOString(),
    };
    console.log("[googleAuthSession] calling", diag);

    let data;
    try {
      data = await this.request("/auth/session", {
        method: "POST",
        body: payload,
      });
    } catch (err) {
      const msg = String(err?.message || "");
      console.warn("[googleAuthSession] fetch error", { msg, ...diag });
      // Any of these messages → try XHR fallback before surfacing the error.
      const looksLikeExtensionHook =
        (msg.includes("postMessage") && msg.includes("could not be cloned")) ||
        msg.includes("Failed to fetch") ||
        msg.includes("Network request failed") ||
        msg.includes("Load failed") ||
        msg.includes("NetworkError");
      if (!looksLikeExtensionHook) throw err;
      // XHR fallback — same request, different transport.
      console.warn("[googleAuthSession] fetch failed (\"" + msg + "\"), falling back to XHR");
      try {
        data = await this._xhrJson(url, "POST", payload);
        if (data?.token) this.setToken(data.token);
        return data;
      } catch (xhrErr) {
        // Both transports failed. Surface a friendlier diagnostic.
        const xhrMsg = String(xhrErr?.message || "");
        console.error("[googleAuthSession] BOTH fetch and XHR failed", { fetchMsg: msg, xhrMsg, ...diag });
        if (xhrMsg.includes("status 0") || xhrMsg.includes("Network error") || xhrMsg.includes("Error de red")) {
          throw new Error(
            "No se pudo conectar con el servidor. Comprueba tu conexión a internet, prueba en modo incógnito o desactiva extensiones del navegador (bloqueadores de anuncios, DevTools, antivirus web). Si el problema persiste contacta con soporte.",
          );
        }
        throw xhrErr;
      }
    }
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  // Minimal XHR helper — used ONLY as fallback when fetch is hijacked by
  // a browser extension that breaks structured cloning. Sends/receives JSON,
  // includes credentials, mirrors the error shape of `request()`.
  //
  // Built-in retry (Feb 2026 iter.209): for transient network errors
  // (DNS flake, CDN edge hiccup, intermittent connectivity on cellular),
  // we silently retry ONCE after 1.5s before surfacing "Error de red"
  // to the user. Empirically this resolves the "reintentar a veces
  // funciona" pattern reported in production without user friction.
  _xhrJson(url, method, body, _attempt = 0) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", "application/json");
      const auth = this.getHeaders().Authorization;
      if (auth) xhr.setRequestHeader("Authorization", auth);
      xhr.onload = () => {
        let parsed = null;
        try { parsed = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
        catch { parsed = { detail: xhr.responseText || "" }; }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(parsed);
        } else {
          const e = new Error(parsed?.detail || `Error del servidor (${xhr.status})`);
          e.status = xhr.status;
          e.body = parsed;
          reject(e);
        }
      };
      xhr.onerror = () => {
        // Auto-retry once for transient flakes.
        if (_attempt < 1) {
          console.warn(`[api/_xhrJson] network error on ${method} ${url} — retrying in 1.5s (attempt ${_attempt + 1}/1)`);
          setTimeout(() => {
            this._xhrJson(url, method, body, _attempt + 1).then(resolve, reject);
          }, 1500);
          return;
        }
        console.error(`[api/_xhrJson] network error on ${method} ${url} after retry`, {
          origin: typeof window !== "undefined" ? window.location.origin : "unknown",
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        });
        reject(new Error("Error de red"));
      };
      xhr.send(body);
    });
  }

  async getMe() {
    return this.request("/auth/me");
  }

  async updateMyPreferences(prefs) {
    return this.request("/auth/me/preferences", {
      method: "PATCH",
      body: JSON.stringify(prefs),
    });
  }

  async logout() {
    await this.request("/auth/logout", { method: "POST" });
    this.clearToken();
  }

  // Restaurants
  async getRestaurants() {
    return this.request("/restaurants");
  }

  async getDocumentImpact(restaurantId, documentId) {
    return this.request(
      `/restaurants/${restaurantId}/finance/documents/${documentId}/impact`,
    );
  }

  async setRestaurantBrandingTheme(restaurantId, theme, accentColor = null) {
    const body = { theme };
    if (theme === "custom" && accentColor) body.accent_color = accentColor;
    return this.request(`/restaurants/${restaurantId}/branding/theme`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  async createRestaurant(data) {
    return this.request("/restaurants", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getRestaurant(id) {
    return this.request(`/restaurants/${id}`);
  }

  async updateRestaurant(id, data) {
    return this.request(`/restaurants/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // Restaurant lifecycle (archive/restore/delete/export)
  async getArchivedRestaurants() {
    return this.request("/restaurants/archived");
  }

  async archiveRestaurant(id) {
    return this.request(`/restaurants/${id}/archive`, { method: "POST" });
  }

  async restoreRestaurant(id) {
    return this.request(`/restaurants/${id}/restore`, { method: "POST" });
  }

  async requestDeleteOTP(id) {
    return this.request(`/restaurants/${id}/delete/request-otp`, { method: "POST" });
  }

  async confirmDeleteRestaurant(id, otp) {
    return this.request(`/restaurants/${id}/delete/confirm`, {
      method: "POST",
      body: JSON.stringify({ otp }),
    });
  }

  async exportRestaurantDataBlob(id) {
    // Bypass this.request() because we need a Blob, not JSON.
    const url = `${this.baseUrl}/restaurants/${id}/export`;
    const token = this._token || localStorage.getItem("token");
    const r = await fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) {
      const txt = await r.text();
      throw new Error(txt || `Export failed (${r.status})`);
    }
    return await r.blob();
  }

  // ─── Finance ────────────────────────────────────────────────
  async getFinanceCategories() {
    return this.request("/finance/categories");
  }
  async getFinanceSummary(rid, { from, to, compare } = {}) {
    const qs = new URLSearchParams({ from, to });
    if (compare) qs.append("compare", "true");
    return this.request(`/restaurants/${rid}/finance/summary?${qs}`);
  }
  async getFinanceTrend(rid, months = 12) {
    return this.request(`/restaurants/${rid}/finance/trend?months=${months}`);
  }
  async getFinanceInsights(rid, { from, to, refresh } = {}) {
    const qs = new URLSearchParams({ from, to });
    if (refresh) qs.append("refresh", "true");
    return this.request(`/restaurants/${rid}/finance/insights?${qs}`);
  }
  // ─── Accountant export ──────────────────────────────────────
  async getAccountantEmail(rid) {
    return this.request(`/restaurants/${rid}/finance/accountant-email`);
  }
  async setAccountantEmail(rid, patch) {
    // `patch` may be a plain string (legacy) or {accountant_email, accountant_auto_send}
    const body = typeof patch === "string" ? { accountant_email: patch } : patch;
    return this.request(`/restaurants/${rid}/finance/accountant-email`, {
      method: "PUT", body: JSON.stringify(body),
    });
  }
  async sendFinanceExportEmail(rid, month, emailTo, cc) {
    const qs = new URLSearchParams({ month, send_email: "true" });
    if (emailTo) qs.append("email_to", emailTo);
    if (cc && cc.length) qs.append("email_cc", cc.join(","));
    return this.request(`/restaurants/${rid}/finance/export?${qs}`, { method: "POST" });
  }
  async sendFinanceAnnualExportEmail(rid, year, emailTo) {
    const qs = new URLSearchParams({ year: String(year), send_email: "true" });
    if (emailTo) qs.append("email_to", emailTo);
    return this.request(`/restaurants/${rid}/finance/export-year?${qs}`, { method: "POST" });
  }
  async sendFinanceQuarterExportEmail(rid, year, quarter, emailTo) {
    const qs = new URLSearchParams({
      year: String(year), quarter: String(quarter), send_email: "true",
    });
    if (emailTo) qs.append("email_to", emailTo);
    return this.request(`/restaurants/${rid}/finance/export-quarter?${qs}`, { method: "POST" });
  }
  async listFinanceExportLog(rid, limit = 24) {
    return this.request(`/restaurants/${rid}/finance/export-log?limit=${limit}`);
  }
  // ─── Cash flow forecast ─────────────────────────────────────
  async getCashFlowSettings(rid) {
    return this.request(`/restaurants/${rid}/finance/cash-flow/settings`);
  }
  async setCashFlowSettings(rid, patch) {
    return this.request(`/restaurants/${rid}/finance/cash-flow/settings`, {
      method: "PUT", body: JSON.stringify(patch),
    });
  }
  async getCashFlowForecast(rid, { horizon = 30, startingBalance } = {}) {
    const qs = new URLSearchParams({ horizon: String(horizon) });
    if (startingBalance !== undefined && startingBalance !== null) {
      qs.append("starting_balance", String(startingBalance));
    }
    return this.request(`/restaurants/${rid}/finance/cash-flow?${qs}`);
  }
  async getRecurringExpenses(rid, { horizon = 90 } = {}) {
    return this.request(`/restaurants/${rid}/finance/recurring-expenses?horizon=${horizon}`);
  }
  async upsertRecurringOverride(rid, patternKey, payload) {
    const enc = encodeURIComponent(patternKey);
    return this.request(`/restaurants/${rid}/finance/recurring-overrides/${enc}`, {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    });
  }
  async deleteRecurringOverride(rid, patternKey) {
    const enc = encodeURIComponent(patternKey);
    return this.request(`/restaurants/${rid}/finance/recurring-overrides/${enc}`, {
      method: "DELETE",
    });
  }
  async getRecurringOverrideHistory(rid, patternKey, limit = 10) {
    const enc = encodeURIComponent(patternKey);
    return this.request(`/restaurants/${rid}/finance/recurring-overrides/${enc}/history?limit=${limit}`);
  }
  async getDuplicateDocuments(rid) {
    return this.request(`/restaurants/${rid}/finance/duplicates`);
  }
  async resolveDuplicates(rid, deleteIds) {
    return this.request(`/restaurants/${rid}/finance/duplicates/resolve`, {
      method: "POST", body: JSON.stringify({ delete_ids: deleteIds }),
    });
  }
  async getWeeklyBrief(rid) {
    return this.request(`/restaurants/${rid}/finance/weekly-brief`);
  }
  // Returns a Blob for browser download — doesn't use `.request()` because
  // the response is binary (application/zip), not JSON.
  async downloadFinanceExport(rid, month) {
    const url = `${this.baseUrl}/restaurants/${rid}/finance/export?month=${encodeURIComponent(month)}`;
    const resp = await fetch(url, {
      method: "POST",
      headers: this.getHeaders(),
      credentials: "include",
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || `Error ${resp.status}`);
    }
    const cd = resp.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename = match ? match[1] : `export_${month}.zip`;
    const blob = await resp.blob();
    return { blob, filename };
  }
  async downloadFinanceAnnualExport(rid, year) {
    const url = `${this.baseUrl}/restaurants/${rid}/finance/export-year?year=${year}`;
    const resp = await fetch(url, {
      method: "POST",
      headers: this.getHeaders(),
      credentials: "include",
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || `Error ${resp.status}`);
    }
    const cd = resp.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename = match ? match[1] : `export_anual_${year}.zip`;
    const blob = await resp.blob();
    return { blob, filename };
  }
  async downloadFinanceQuarterExport(rid, year, quarter) {
    const url = `${this.baseUrl}/restaurants/${rid}/finance/export-quarter?year=${year}&quarter=${quarter}`;
    const resp = await fetch(url, {
      method: "POST",
      headers: this.getHeaders(),
      credentials: "include",
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || `Error ${resp.status}`);
    }
    const cd = resp.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename = match ? match[1] : `export_${year}_Q${quarter}.zip`;
    const blob = await resp.blob();
    return { blob, filename };
  }
  // ─── Notification preferences ───────────────────────────────
  async listNotificationPreferences(rid) {
    return this.request(`/restaurants/${rid}/notification-preferences`);
  }
  async updateNotificationPreference(rid, channel, enabled) {
    return this.request(`/restaurants/${rid}/notification-preferences/${channel}`, {
      method: "PATCH", body: JSON.stringify({ enabled }),
    });
  }
  async sendNotificationNow(rid, channel) {
    return this.request(`/restaurants/${rid}/notification-preferences/${channel}/send-now`, { method: "POST" });
  }
  async listFinanceDocuments(rid, params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/restaurants/${rid}/finance/documents${qs ? "?" + qs : ""}`);
  }
  async duplicatesPreventedSummary(rid, days = 30) {
    return this.request(`/restaurants/${rid}/finance/duplicates-prevented?days=${days}`);
  }
  async previewDocumentInventorySync(rid, docId) {
    return this.request(`/restaurants/${rid}/finance/documents/${docId}/sync-to-inventory/preview`);
  }
  async listRecentAutoInventoryEntries(rid, limit = 10) {
    return this.request(`/restaurants/${rid}/finance/recent-auto-entries?limit=${limit}`);
  }
  async listRecentPriceHikes(rid, days = 30, limit = 20) {
    return this.request(`/restaurants/${rid}/inventory/price-hikes?days=${days}&limit=${limit}`);
  }
  async getWebhooksHealth(rid) {
    return this.request(`/restaurants/${rid}/webhooks/health`);
  }
  async listWebhookEvents(rid, { status = null, vendor = null, limit = 50 } = {}) {
    const qs = new URLSearchParams({ limit });
    if (status) qs.append("status", status);
    if (vendor) qs.append("vendor", vendor);
    return this.request(`/restaurants/${rid}/webhooks/events?${qs.toString()}`);
  }
  async retryWebhookEvent(rid, eventId) {
    return this.request(`/restaurants/${rid}/webhooks/events/${eventId}/retry`, { method: "POST" });
  }
  async getPriceHikeSuggestion(rid, ingredientId) {
    return this.request(`/restaurants/${rid}/inventory/price-hikes/${ingredientId}/suggestion`);
  }
  async syncDocumentToInventory(rid, docId, { force = false, autoCreate = true } = {}) {
    const qs = new URLSearchParams();
    if (force) qs.append("force", "true");
    qs.append("auto_create", autoCreate ? "true" : "false");
    return this.request(`/restaurants/${rid}/finance/documents/${docId}/sync-to-inventory?${qs.toString()}`, {
      method: "POST",
    });
  }
  async getFinanceDocument(rid, docId) {
    return this.request(`/restaurants/${rid}/finance/documents/${docId}`);
  }
  async updateFinanceDocument(rid, docId, data) {
    return this.request(`/restaurants/${rid}/finance/documents/${docId}`, {
      method: "PUT", body: JSON.stringify(data),
    });
  }
  async deleteFinanceDocument(rid, docId) {
    return this.request(`/restaurants/${rid}/finance/documents/${docId}`, { method: "DELETE" });
  }
  async uploadFinanceDocument(rid, file, { force = false } = {}) {
    const form = new FormData();
    form.append("file", file);
    if (force) form.append("force", "true");
    const token = this._token || localStorage.getItem("token");
    const r = await fetch(`${this.baseUrl}/restaurants/${rid}/finance/documents/upload`, {
      method: "POST",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (r.status === 409) {
      const body = await r.clone().json().catch(() => ({}));
      const err = new Error(body?.detail?.message || "Documento duplicado");
      err.code = "duplicate_document";
      err.duplicate = body?.detail?.duplicate || null;
      throw err;
    }
    if (!r.ok) {
      // Defensive: use clone() in case the body was somehow already consumed
      // (service worker, extension interceptor). Try JSON first, then text.
      let msg = "";
      try {
        const body = await r.clone().json();
        msg = body?.detail?.message || body?.detail || body?.message || "";
        if (typeof msg !== "string") msg = JSON.stringify(msg);
      } catch {
        try { msg = await r.clone().text(); } catch { /* ignore */ }
      }
      throw new Error(msg || `Upload failed (${r.status})`);
    }
    return await r.json();
  }
  financeDocumentFileUrl(rid, docId) {
    return `${this.baseUrl}/restaurants/${rid}/finance/documents/${docId}/file`;
  }
  financeDocumentPreviewUrl(rid, docId, page = 0) {
    return `${this.baseUrl}/restaurants/${rid}/finance/documents/${docId}/preview?page=${page}`;
  }
  async reanalyzeFinanceDocument(rid, docId, corrections = null) {
    return this.request(
      `/restaurants/${rid}/finance/documents/${docId}/reanalyze`,
      { method: "POST", body: JSON.stringify({ corrections }) },
    );
  }
  async linkDocumentToCashPayout(rid, docId, { sessionId, payoutIndex }) {
    return this.request(
      `/restaurants/${rid}/finance/documents/${docId}/link-cash-payout`,
      { method: "POST", body: JSON.stringify({
        session_id: sessionId, payout_index: payoutIndex,
      })},
    );
  }

  // ───────── Operational closings (cash arqueo + sales-day + timeline) ─────────
  async getCurrentCashSession(rid, day = null) {
    const qs = day ? `?day=${day}` : "";
    return this.request(`/restaurants/${rid}/closings/cash/current${qs}`);
  }
  async openCashSession(rid, data) {
    return this.request(`/restaurants/${rid}/closings/cash/open`, {
      method: "POST", body: JSON.stringify(data),
    });
  }
  async closeCashSession(rid, sessionId, data) {
    return this.request(`/restaurants/${rid}/closings/cash/${sessionId}/close`, {
      method: "POST", body: JSON.stringify(data),
    });
  }
  async listCashSessions(rid, { from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.append("from", from);
    if (to) qs.append("to", to);
    return this.request(`/restaurants/${rid}/closings/cash${qs.toString() ? "?" + qs : ""}`);
  }
  async previewSalesDay(rid, day = null) {
    const qs = day ? `?day=${day}` : "";
    return this.request(`/restaurants/${rid}/closings/sales-day/preview${qs}`);
  }
  async closeSalesDay(rid, data) {
    return this.request(`/restaurants/${rid}/closings/sales-day`, {
      method: "POST", body: JSON.stringify(data),
    });
  }
  async getClosingsTimeline(rid, year, month) {
    return this.request(`/restaurants/${rid}/closings/timeline?year=${year}&month=${month}`);
  }
  async getPendingReceipts(rid, daysBack = 30) {
    return this.request(`/restaurants/${rid}/closings/pending-receipts?days_back=${daysBack}`);
  }
  async downloadDailyReportPdf(rid, day) {
    const filename = `cierre_diario_${day}.pdf`;
    return this.downloadFile(
      `/restaurants/${rid}/closings/daily-report/pdf?day=${encodeURIComponent(day)}`,
      filename,
    );
  }
  async sendDailyReportToAccountant(rid, { day, emailTo, cc } = {}) {
    return this.request(`/restaurants/${rid}/closings/daily-report/send`, {
      method: "POST",
      body: JSON.stringify({ day, email_to: emailTo || undefined, cc: cc || undefined }),
    });
  }
  async previewDailyReportEmail(rid, { day, emailTo, cc } = {}) {
    return this.request(`/restaurants/${rid}/closings/daily-report/preview`, {
      method: "POST",
      body: JSON.stringify({ day, email_to: emailTo || undefined, cc: cc || undefined }),
    });
  }
  async previewMonthlyExportEmail(rid, { month, emailTo, cc } = {}) {
    const qs = new URLSearchParams({ month });
    if (emailTo) qs.append("email_to", emailTo);
    if (cc && cc.length) qs.append("email_cc", cc.join(","));
    return this.request(`/restaurants/${rid}/finance/export/preview?${qs.toString()}`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }
  async getDailyReportHistory(rid, limit = 30) {
    return this.request(`/restaurants/${rid}/closings/daily-report/history?limit=${limit}`);
  }
  async getMonthlyPreCloseChecklist(rid, { year, month } = {}) {
    return this.request(`/restaurants/${rid}/closings/monthly-checklist?year=${year}&month=${month}`);
  }
  // Inventory closing snapshots
  async createInventorySnapshot(rid, { day, notes, linkedCountSessionRef } = {}) {
    return this.request(`/restaurants/${rid}/inventory/snapshots`, {
      method: "POST",
      body: JSON.stringify({
        day: day || undefined,
        notes: notes || undefined,
        linked_count_session_ref: linkedCountSessionRef || undefined,
      }),
    });
  }
  async listInventorySnapshots(rid, { from, to, limit = 60 } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.append("from", from);
    if (to) qs.append("to", to);
    if (limit) qs.append("limit", String(limit));
    return this.request(`/restaurants/${rid}/inventory/snapshots?${qs.toString()}`);
  }
  async getInventorySnapshot(rid, snapshotId) {
    return this.request(`/restaurants/${rid}/inventory/snapshots/${snapshotId}`);
  }
  // Inventory-drop alert (hidden shrinkage detector)
  async getInventoryDropAlertConfig(rid) {
    return this.request(`/restaurants/${rid}/inventory/drop-alert/config`);
  }
  async updateInventoryDropAlertConfig(rid, patch) {
    return this.request(`/restaurants/${rid}/inventory/drop-alert/config`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }
  async runInventoryDropAlertNow(rid, { sendEmail = false } = {}) {
    return this.request(`/restaurants/${rid}/inventory/drop-alert/run-now?send_email=${sendEmail}`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }
  async getInventoryDropAlertHistory(rid, limit = 30) {
    return this.request(`/restaurants/${rid}/inventory/drop-alert/history?limit=${limit}`);
  }
  async analyzeInventoryDropAlert(rid, { force = false } = {}) {
    return this.request(`/restaurants/${rid}/inventory/drop-alert/analyze?force=${force}`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }
  async setDropAlertFeedback(rid, { target, index, status = null, note = null } = {}) {
    return this.request(`/restaurants/${rid}/inventory/drop-alert/feedback`, {
      method: "POST",
      body: JSON.stringify({ target, index, status, note }),
    });
  }
  async getDropAlertLearnings(rid, limit = 10) {
    return this.request(`/restaurants/${rid}/inventory/drop-alert/learnings?limit=${limit}`);
  }
  async deleteInventorySnapshot(rid, snapshotId) {
    return this.request(`/restaurants/${rid}/inventory/snapshots/${snapshotId}`, {
      method: "DELETE",
    });
  }
  // Production logs
  async upsertProductionLog(rid, { day, items, wasteItems, notes } = {}) {
    return this.request(`/restaurants/${rid}/production-logs`, {
      method: "POST",
      body: JSON.stringify({
        day: day || undefined,
        items: items || [],
        waste_items: wasteItems || [],
        notes: notes || undefined,
      }),
    });
  }
  async listProductionLogs(rid, { from, to, limit = 60 } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.append("from", from);
    if (to) qs.append("to", to);
    if (limit) qs.append("limit", String(limit));
    return this.request(`/restaurants/${rid}/production-logs?${qs.toString()}`);
  }
  async getProductionLogByDay(rid, day) {
    return this.request(`/restaurants/${rid}/production-logs/${day}`);
  }
  async deleteProductionLog(rid, logId) {
    return this.request(`/restaurants/${rid}/production-logs/${logId}`, {
      method: "DELETE",
    });
  }
  async getExpiringProductionItems(rid, days = 3) {
    return this.request(`/restaurants/${rid}/production-logs/expiring?days=${days}`);
  }
  async getTodayClosingStatus(rid, day = null) {
    const qs = day ? `?day=${day}` : "";
    return this.request(`/restaurants/${rid}/closings/today-status${qs}`);
  }
  async getFoodCostSeries(rid, days = 30, end = null) {
    const qs = `?days=${days}${end ? `&end=${end}` : ""}`;
    return this.request(`/restaurants/${rid}/finance/food-cost${qs}`);
  }
  // Monthly fiscal locks
  async getMonthlyLocks(rid) {
    return this.request(`/restaurants/${rid}/closings/monthly-locks`);
  }
  // Multi-register + shifts (Feature #124)
  async listCashRegisters(rid) {
    return this.request(`/restaurants/${rid}/cash-registers`);
  }
  async createCashRegister(rid, data) {
    return this.request(`/restaurants/${rid}/cash-registers`, {
      method: "POST", body: JSON.stringify(data),
    });
  }
  async updateCashRegister(rid, registerId, data) {
    return this.request(`/restaurants/${rid}/cash-registers/${registerId}`, {
      method: "PUT", body: JSON.stringify(data),
    });
  }
  async deleteCashRegister(rid, registerId) {
    return this.request(`/restaurants/${rid}/cash-registers/${registerId}`, {
      method: "DELETE",
    });
  }
  async getShifts(rid) {
    return this.request(`/restaurants/${rid}/shifts`);
  }
  async updateShifts(rid, shifts) {
    return this.request(`/restaurants/${rid}/shifts`, {
      method: "PUT", body: JSON.stringify({ shifts }),
    });
  }
  async upsertDatafonoRecord(rid, data) {
    return this.request(`/restaurants/${rid}/closings/datafono`, {
      method: "POST", body: JSON.stringify(data),
    });
  }
  async ocrDatafonoTicket(rid, { imageB64, mimeType = "image/jpeg", sessionDate = null } = {}) {
    return this.request(`/restaurants/${rid}/closings/datafono/ocr`, {
      method: "POST",
      body: JSON.stringify({
        image_b64: imageB64,
        mime_type: mimeType,
        session_date: sessionDate,
      }),
    });
  }
  async createZClose(rid, registerId, { sessionDate, shiftId = null, cashSessionId = null } = {}) {
    return this.request(`/restaurants/${rid}/cash-registers/${registerId}/z-close`, {
      method: "POST",
      body: JSON.stringify({
        session_date: sessionDate,
        shift_id: shiftId,
        cash_session_id: cashSessionId,
      }),
    });
  }
  async listZCloses(rid, registerId, { fromDay = null, toDay = null, limit = 100 } = {}) {
    const qs = new URLSearchParams();
    if (fromDay) qs.append("from_day", fromDay);
    if (toDay) qs.append("to_day", toDay);
    qs.append("limit", limit);
    return this.request(`/restaurants/${rid}/cash-registers/${registerId}/z-closes?${qs}`);
  }
  async verifyZChain(rid, registerId) {
    return this.request(`/restaurants/${rid}/cash-registers/${registerId}/z-closes/verify`);
  }
  async getRegisterDashboard(rid, registerId, days = 30) {
    return this.request(`/restaurants/${rid}/cash-registers/${registerId}/dashboard?days=${days}`);
  }
  // Z-certificate email (push-style fiscal audit trail)
  async getZCertEmailSettings(rid) {
    return this.request(`/restaurants/${rid}/z-cert-email/settings`);
  }
  async updateZCertEmailSettings(rid, patch) {
    return this.request(`/restaurants/${rid}/z-cert-email/settings`, {
      method: "PUT", body: JSON.stringify(patch),
    });
  }
  async resendZCertificate(rid, zCloseId, { emailTo = null } = {}) {
    const qs = emailTo ? `?email_to=${encodeURIComponent(emailTo)}` : "";
    return this.request(`/restaurants/${rid}/z-closes/${zCloseId}/resend${qs}`, {
      method: "POST",
    });
  }
  async getZCertEmailLog(rid, zCloseId) {
    return this.request(`/restaurants/${rid}/z-closes/${zCloseId}/email-log`);
  }
  // Build a browser-ready URL for direct download (uses browser auth cookie if any,
  // else the caller must append the token. Simpler path: fetch + blob + saveAs.)
  async downloadZCertificate(rid, zCloseId) {
    const url = `${this.baseUrl}/restaurants/${rid}/z-closes/${zCloseId}/certificate`;
    const res = await fetch(url, {
      headers: this.getAuthOnlyHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = /filename="?([^";]+)"?/.exec(cd);
    const filename = m ? m[1] : `z-certificate-${zCloseId}.json`;
    return { blob, filename };
  }
  async downloadAnnualZBook(rid, { year = null, registerId = null } = {}) {
    const qs = new URLSearchParams();
    if (year) qs.append("year", year);
    if (registerId) qs.append("register_id", registerId);
    const url = `${this.baseUrl}/restaurants/${rid}/z-closes/annual-book${qs.toString() ? "?" + qs : ""}`;
    const res = await fetch(url, {
      headers: this.getAuthOnlyHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = /filename="?([^";]+)"?/.exec(cd);
    const filename = m ? m[1] : `libro-z-${year || "actual"}.pdf`;
    return { blob, filename };
  }
  async sendAnnualZBook(rid, { year = null, emailTo = null } = {}) {
    const qs = new URLSearchParams();
    if (year) qs.append("year", year);
    if (emailTo) qs.append("email_to", emailTo);
    return this.request(`/restaurants/${rid}/z-closes/annual-book/send${qs.toString() ? "?" + qs : ""}`, {
      method: "POST",
    });
  }
  async getAnnualBookEmailLog(rid, { year = null } = {}) {
    const qs = year ? `?year=${year}` : "";
    return this.request(`/restaurants/${rid}/z-closes/annual-book/email-log${qs}`);
  }
  async sendMonthlyExecSummary(rid, { year = null, month = null, emailTo = null } = {}) {
    const qs = new URLSearchParams();
    if (year) qs.append("year", year);
    if (month) qs.append("month", month);
    if (emailTo) qs.append("email_to", emailTo);
    return this.request(`/restaurants/${rid}/monthly-exec-summary/send${qs.toString() ? "?" + qs : ""}`, {
      method: "POST",
    });
  }
  async getMonthlyExecSummaryLog(rid, { period = null } = {}) {
    const qs = period ? `?period=${period}` : "";
    return this.request(`/restaurants/${rid}/monthly-exec-summary/email-log${qs}`);
  }
  // Fiscal notifications center
  async getFiscalNotificationsUnresolved(rid) {
    return this.request(`/restaurants/${rid}/fiscal-notifications/unresolved-count`);
  }
  // Auto-generated draft orders count (sidebar badge)
  async getDraftOrdersCount(rid) {
    return this.request(`/restaurants/${rid}/orders/draft-count`);
  }
  async acknowledgeFiscalNotification(rid, { type, refId = null }) {
    return this.request(`/restaurants/${rid}/fiscal-notifications/acknowledge`, {
      method: "POST",
      body: JSON.stringify({ type, ref_id: refId }),
    });
  }
  async getDatafonoRecord(rid, { day = null, cashRegisterId = null, shiftId = null } = {}) {
    const qs = new URLSearchParams();
    if (day) qs.append("day", day);
    if (cashRegisterId) qs.append("cash_register_id", cashRegisterId);
    if (shiftId) qs.append("shift_id", shiftId);
    const s = qs.toString();
    return this.request(`/restaurants/${rid}/closings/datafono${s ? `?${s}` : ""}`);
  }
  async requestLockOtp(rid, { year, month }) {
    return this.request(`/restaurants/${rid}/closings/monthly-lock/request-otp`, {
      method: "POST",
      body: JSON.stringify({ year, month }),
    });
  }
  async confirmMonthlyLock(rid, { year, month, otp }) {
    return this.request(`/restaurants/${rid}/closings/monthly-lock`, {
      method: "POST",
      body: JSON.stringify({ year, month, otp }),
    });
  }
  async requestUnlockOtp(rid, { year, month }) {
    return this.request(`/restaurants/${rid}/closings/monthly-unlock/request-otp`, {
      method: "POST",
      body: JSON.stringify({ year, month }),
    });
  }
  async confirmMonthlyUnlock(rid, { year, month, otp, reason, requestId = null }) {
    return this.request(`/restaurants/${rid}/closings/monthly-unlock`, {
      method: "POST",
      body: JSON.stringify({ year, month, otp, reason, request_id: requestId }),
    });
  }
  async requestUnlockFromManager(rid, { year, month, reason }) {
    return this.request(`/restaurants/${rid}/closings/monthly-unlock/request`, {
      method: "POST",
      body: JSON.stringify({ year, month, reason }),
    });
  }
  async rejectUnlockRequest(rid, requestId) {
    return this.request(`/restaurants/${rid}/closings/monthly-unlock/requests/${requestId}/reject`, {
      method: "POST",
    });
  }
  async linkProductionItem(rid, logId, itemId, { linkType, linkedId } = {}) {
    return this.request(
      `/restaurants/${rid}/production-logs/${logId}/items/${itemId}/link`,
      {
        method: "POST",
        body: JSON.stringify({
          link_type: linkType,
          linked_id: linkedId || null,
        }),
      },
    );
  }
  // Batch recipes (fichas técnicas de producción)
  async listBatchRecipes(rid, { archived = false } = {}) {
    const q = archived ? "?archived=true" : "";
    return this.request(`/restaurants/${rid}/batch-recipes${q}`);
  }
  async getBatchRecipe(rid, batchRecipeId) {
    return this.request(`/restaurants/${rid}/batch-recipes/${batchRecipeId}`);
  }
  async createBatchRecipe(rid, data) {
    return this.request(`/restaurants/${rid}/batch-recipes`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  async updateBatchRecipe(rid, batchRecipeId, data) {
    return this.request(`/restaurants/${rid}/batch-recipes/${batchRecipeId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }
  async deleteBatchRecipe(rid, batchRecipeId) {
    // Permanent deletion — aligned with /dishes/{id}/permanent and
    // /menus/{id}/permanent. Only succeeds if the recipe is archived.
    return this.request(`/restaurants/${rid}/batch-recipes/${batchRecipeId}/permanent`, {
      method: "DELETE",
    });
  }
  async archiveBatchRecipe(rid, batchRecipeId) {
    return this.request(`/restaurants/${rid}/batch-recipes/${batchRecipeId}/archive`, {
      method: "POST",
    });
  }
  async restoreBatchRecipe(rid, batchRecipeId) {
    return this.request(`/restaurants/${rid}/batch-recipes/${batchRecipeId}/restore`, {
      method: "POST",
    });
  }
  // Legacy alias — older callers still using `unarchiveBatchRecipe` keep working.
  async unarchiveBatchRecipe(rid, batchRecipeId) {
    return this.restoreBatchRecipe(rid, batchRecipeId);
  }
  async suggestBatchRecipe(rid, { name, yieldQty, yieldUnit } = {}) {
    return this.request(`/restaurants/${rid}/batch-recipes/ai-suggest`, {
      method: "POST",
      body: JSON.stringify({
        name,
        yield_qty: yieldQty,
        yield_unit: yieldUnit,
      }),
    });
  }
  async listExpenses(rid, { from, to, category } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.append("from", from);
    if (to) qs.append("to", to);
    if (category) qs.append("category", category);
    return this.request(`/restaurants/${rid}/finance/expenses${qs.toString() ? "?" + qs : ""}`);
  }
  async createExpense(rid, data, { force = false } = {}) {
    const path = `/restaurants/${rid}/finance/expenses${force ? "?force=true" : ""}`;
    try {
      return await this.request(path, {
        method: "POST", body: JSON.stringify(data),
      });
    } catch (err) {
      if (err?.status === 409 && err?.detail?.code === "duplicate_expense") {
        const dupErr = new Error(err.detail.message || "Gasto duplicado");
        dupErr.code = "duplicate_expense";
        dupErr.duplicate = err.detail.duplicate || null;
        throw dupErr;
      }
      throw err;
    }
  }
  /**
   * Persist an invoice already OCR-processed by the Inventory flow as a
   * finance_document + linked expense. Uses multipart so the original file
   * is stored (PDF/image) and later shown in the Bandeja + export ZIP.
   */
  async createDocumentFromInventory(rid, file, invoiceData, { force = false } = {}) {
    const form = new FormData();
    form.append("file", file, file.name || "factura");
    form.append("invoice_data", JSON.stringify(invoiceData || {}));
    if (force) form.append("force", "true");
    const url = `${this.baseUrl}/restaurants/${rid}/finance/documents/from-inventory`;
    const resp = await fetch(url, {
      method: "POST",
      headers: this.getAuthOnlyHeaders(),
      body: form,
      credentials: "include",
    });
    if (resp.status === 409) {
      const body = await resp.clone().json().catch(() => ({}));
      const err = new Error(body?.detail?.message || "Documento duplicado");
      err.code = "duplicate_document";
      err.duplicate = body?.detail?.duplicate || null;
      throw err;
    }
    if (!resp.ok) {
      const err = await resp.clone().json().catch(() => ({}));
      throw new Error(err.detail || `Error ${resp.status}`);
    }
    return resp.json();
  }
  async updateExpense(rid, expenseId, data) {
    return this.request(`/restaurants/${rid}/finance/expenses/${expenseId}`, {
      method: "PUT", body: JSON.stringify(data),
    });
  }
  async deleteExpense(rid, expenseId) {
    return this.request(`/restaurants/${rid}/finance/expenses/${expenseId}`, { method: "DELETE" });
  }

  // ─── Bank import & reconciliation ───────────────────────────
  async bankImportCSV(rid, file) {
    const form = new FormData();
    form.append("file", file);
    const token = this._token || localStorage.getItem("token");
    const r = await fetch(`${this.baseUrl}/restaurants/${rid}/finance/bank-import`, {
      method: "POST",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!r.ok) {
      const txt = await r.text();
      throw new Error(txt || `Import failed (${r.status})`);
    }
    return await r.json();
  }
  async listBankTransactions(rid, { status, from, to, import_id } = {}) {
    const qs = new URLSearchParams();
    if (status) qs.append("status", status);
    if (from) qs.append("from", from);
    if (to) qs.append("to", to);
    if (import_id) qs.append("import_id", import_id);
    return this.request(`/restaurants/${rid}/finance/bank-transactions${qs.toString() ? "?" + qs : ""}`);
  }
  async getBankTransactionStats(rid) {
    return this.request(`/restaurants/${rid}/finance/bank-transactions/stats`);
  }
  async getBankTransactionCandidates(rid, txId) {
    return this.request(`/restaurants/${rid}/finance/bank-transactions/${txId}/candidates`);
  }
  async matchBankTransaction(rid, txId, targetType, targetId) {
    return this.request(`/restaurants/${rid}/finance/bank-transactions/${txId}/match`, {
      method: "POST",
      body: JSON.stringify({ target_type: targetType, target_id: targetId }),
    });
  }
  async unmatchBankTransaction(rid, txId) {
    return this.request(`/restaurants/${rid}/finance/bank-transactions/${txId}/unmatch`, { method: "POST" });
  }
  async ignoreBankTransaction(rid, txId) {
    return this.request(`/restaurants/${rid}/finance/bank-transactions/${txId}/ignore`, { method: "POST" });
  }
  async createExpenseFromBank(rid, txId, data) {
    return this.request(`/restaurants/${rid}/finance/bank-transactions/${txId}/create-expense`, {
      method: "POST", body: JSON.stringify(data),
    });
  }
  async aiClassifyBankTransaction(rid, txId) {
    return this.request(`/restaurants/${rid}/finance/bank-transactions/${txId}/ai-classify`, { method: "POST" });
  }
  async deleteBankTransaction(rid, txId) {
    return this.request(`/restaurants/${rid}/finance/bank-transactions/${txId}`, { method: "DELETE" });
  }
  async listBankImports(rid) {
    return this.request(`/restaurants/${rid}/finance/bank-imports`);
  }
  async listBankRules(rid) {
    return this.request(`/restaurants/${rid}/finance/bank-rules`);
  }
  async updateBankRule(rid, ruleId, data) {
    return this.request(`/restaurants/${rid}/finance/bank-rules/${ruleId}`, {
      method: "PATCH", body: JSON.stringify(data),
    });
  }
  async deleteBankRule(rid, ruleId) {
    return this.request(`/restaurants/${rid}/finance/bank-rules/${ruleId}`, { method: "DELETE" });
  }

  // Menus
  async getMenus(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/menus`);
  }

  async createMenu(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/menus`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Categories
  async getCategories(menuId) {
    return this.request(`/menus/${menuId}/categories`);
  }

  async createCategory(menuId, data) {
    return this.request(`/menus/${menuId}/categories`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Dishes
  async getDishes(menuId, { archived = false } = {}) {
    const qs = archived ? "?archived=true" : "";
    return this.request(`/menus/${menuId}/dishes${qs}`);
  }

  async createDish(menuId, data) {
    return this.request(`/menus/${menuId}/dishes`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async createDishesBulk(menuId, dishes) {
    return this.request(`/menus/${menuId}/dishes/bulk`, {
      method: "POST",
      body: JSON.stringify({ dishes }),
    });
  }

  async updateMenu(menuId, data) {
    return this.request(`/menus/${menuId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getAllDishesForRestaurant(restaurantId) {
    const menus = await this.getMenus(restaurantId);
    const allDishes = [];
    await Promise.allSettled(
      menus.map(async (menu) => {
        try {
          const dishes = await this.getDishes(menu.menu_id);
          allDishes.push(...dishes);
        } catch { /* skip failed menus */ }
      })
    );
    return allDishes;
  }

  async updateDish(dishId, data) {
    return this.request(`/dishes/${dishId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async assignStockBeverages(restaurantId, dishIds) {
    return this.request(`/restaurants/${restaurantId}/ai/assign-stock-beverages`, {
      method: "POST",
      body: JSON.stringify({ dish_ids: dishIds }),
    });
  }

  async lookupBarcode(code) {
    return this.request(`/barcodes/lookup?code=${encodeURIComponent(code)}`);
  }

  /**
   * "Synchronous" image regeneration from the UX perspective: enqueues to the
   * internal AI priority lane and polls the job status until ready.
   * Accepts an AbortSignal so the user can cancel mid-wait.
   * Resolves with { image_url, dish_id }. Rejects on failure or abort.
   */
  async generateDishImageSync(dishId, { signal, onEnqueued } = {}) {
    // 1) Enqueue
    const enqueueRes = await this.request(`/dishes/${dishId}/generate-image-sync`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    const jobId = enqueueRes?.job_id;
    if (!jobId) throw new Error("No se pudo iniciar la generación");
    onEnqueued?.(jobId);

    // 2) Poll
    const started = Date.now();
    const MAX_WAIT_MS = 120_000;
    const INTERVAL_MS = 800;
    while (true) {
      if (signal?.aborted) {
        const err = new Error("Cancelado por el cliente");
        err.name = "AbortError";
        throw err;
      }
      if (Date.now() - started > MAX_WAIT_MS) {
        throw new Error("Tiempo de espera agotado");
      }
      await new Promise(r => setTimeout(r, INTERVAL_MS));
      if (signal?.aborted) {
        const err = new Error("Cancelado por el cliente");
        err.name = "AbortError";
        throw err;
      }
      let job;
      try {
        job = await this.request(`/dishes/image-job/${jobId}`);
      } catch {
        continue; // transient — retry
      }
      if (job?.status === "completed" && job.image_url) {
        return { image_url: job.image_url, dish_id: dishId, job_id: jobId };
      }
      if (job?.status === "failed") {
        throw new Error(job.error || "La IA no pudo generar la imagen");
      }
    }
  }

  async updateDishImage(dishId, imageUrl) {
    return this.request(`/dishes/${dishId}/image`, {
      method: "PATCH",
      body: JSON.stringify({ image_url: imageUrl }),
    });
  }

  async deleteDish(dishId) {
    return this.request(`/dishes/${dishId}`, {
      method: "DELETE",
    });
  }

  async archiveDish(dishId) {
    return this.request(`/dishes/${dishId}/archive`, { method: "POST" });
  }

  async restoreDish(dishId) {
    return this.request(`/dishes/${dishId}/restore`, { method: "POST" });
  }

  async deleteDishPermanent(dishId) {
    return this.request(`/dishes/${dishId}/permanent`, { method: "DELETE" });
  }

  async getAllDishesForRestaurantArchived(restaurantId) {
    const menus = await this.getMenus(restaurantId);
    const allDishes = [];
    await Promise.allSettled(
      menus.map(async (menu) => {
        try {
          const dishes = await this.getDishes(menu.menu_id, { archived: true });
          allDishes.push(...dishes);
        } catch { /* skip failed menus */ }
      })
    );
    return allDishes;
  }

  // Ingredients
  async getIngredients(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/ingredients`);
  }

  async createIngredient(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/ingredients`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateIngredient(ingredientId, data) {
    return this.request(`/ingredients/${ingredientId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteIngredient(ingredientId) {
    return this.request(`/ingredients/${ingredientId}`, {
      method: "DELETE",
    });
  }

  async getIngredientImage(ingredientId) {
    return this.request(`/ingredients/${ingredientId}/image`);
  }

  async reconcileInventory(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/inventory/reconcile`, { method: "POST" });
  }

  // ── Ingredient Formats (Fase 3) ───────────────────────────────────
  async getIngredientFormats(ingredientId) {
    return this.request(`/ingredients/${ingredientId}/formats`);
  }

  async createIngredientFormat(ingredientId, data) {
    return this.request(`/ingredients/${ingredientId}/formats`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateIngredientFormat(ingredientId, formatId, data) {
    return this.request(`/ingredients/${ingredientId}/formats/${formatId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteIngredientFormat(ingredientId, formatId) {
    return this.request(`/ingredients/${ingredientId}/formats/${formatId}`, {
      method: "DELETE",
    });
  }

  async seedIngredientFormatPresets(ingredientId) {
    return this.request(`/ingredients/${ingredientId}/formats/seed-presets`, {
      method: "POST",
    });
  }

  async suggestFormatPrice(ingredientId, formatId) {
    return this.request(`/ingredients/${ingredientId}/formats/${formatId}/suggest-price`, {
      method: "POST",
    });
  }

  // ── Count sessions (Inventario Vivo Fase 4) ───────────────────────
  async getTheoreticalStocks(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/stock-movements/theoretical-stock`);
  }

  async createStockMovement(restaurantId, payload) {
    return this.request(`/restaurants/${restaurantId}/stock-movements`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async submitCountSession(restaurantId, payload) {
    return this.request(`/restaurants/${restaurantId}/inventory/count-session`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async analyzeShelfPhoto(restaurantId, imageBase64, zone) {
    return this.request(`/restaurants/${restaurantId}/inventory/shelf-analyze`, {
      method: "POST",
      body: JSON.stringify({ image_base64: imageBase64, zone }),
    });
  }

  async saveVisionAlias(restaurantId, detectedName, ingredientId) {
    return this.request(`/restaurants/${restaurantId}/inventory/vision-alias`, {
      method: "POST",
      body: JSON.stringify({ detected_name: detectedName, ingredient_id: ingredientId }),
    });
  }

  // Recipes
  async getRecipe(dishId) {
    return this.request(`/dishes/${dishId}/recipe`);
  }

  async createRecipe(dishId, data) {
    return this.request(`/dishes/${dishId}/recipe`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Suppliers
  async getSuppliers(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/suppliers`);
  }

  async createSupplier(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/suppliers`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateSupplier(supplierId, data) {
    return this.request(`/suppliers/${supplierId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteSupplier(supplierId) {
    return this.request(`/suppliers/${supplierId}`, {
      method: "DELETE",
    });
  }

  async suggestSuppliersWithPrompt(restaurantId, { prompt = "", count = 5 } = {}) {
    return this.request(`/restaurants/${restaurantId}/suppliers/suggest`, {
      method: "POST",
      body: JSON.stringify({ prompt, count }),
    });
  }

  async bulkCreateSuppliers(restaurantId, suppliers) {
    return this.request(`/restaurants/${restaurantId}/suppliers/bulk-create`, {
      method: "POST",
      body: JSON.stringify({ suppliers }),
    });
  }

  async getSuppliersSavingsRanking(restaurantId, days = 90) {
    return this.request(`/restaurants/${restaurantId}/suppliers/savings-ranking?days=${days}`);
  }

  async getSuppliersScorecard(restaurantId, days = 90) {
    return this.request(`/restaurants/${restaurantId}/suppliers/scorecard?days=${days}`);
  }

  async getSavingsOpportunity(restaurantId, { days = 90, top = 3 } = {}) {
    return this.request(`/restaurants/${restaurantId}/savings-opportunity?days=${days}&top=${top}`);
  }

  // Purchase Orders
  async getOrders(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/orders`);
  }

  async createOrder(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/orders`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateOrderStatus(orderId, status) {
    return this.request(`/orders/${orderId}/status?status=${status}`, {
      method: "PUT",
    });
  }

  async receiveOrder(orderId, payload) {
    return this.request(`/orders/${orderId}/receive`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Customers
  async getCustomers(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/customers`);
  }

  async createCustomer(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/customers`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async deleteCustomer(customerId) {
    return this.request(`/customers/${customerId}`, { method: "DELETE" });
  }

  async addCustomerPoints(customerId, points) {
    return this.request(`/customers/${customerId}/points?points=${points}`, {
      method: "POST",
    });
  }

  async addPurchase(customerId, amount, notes = "") {
    return this.request(`/customers/${customerId}/add-purchase`, {
      method: "POST",
      body: JSON.stringify({ amount, notes }),
    });
  }

  async redeemReward(customerId, rewardId) {
    return this.request(`/customers/${customerId}/redeem-reward`, {
      method: "POST",
      body: JSON.stringify({ reward_id: rewardId }),
    });
  }

  async scanLoyaltyQR(restaurantId, { qr_data, amount = null, visit_only = false }) {
    return this.request(`/restaurants/${restaurantId}/loyalty/scan`, {
      method: "POST",
      body: JSON.stringify({ qr_data, amount, visit_only }),
    });
  }

  async listCustomerTransactions(customerId, { limit = 100, offset = 0 } = {}) {
    return this.request(`/customers/${customerId}/transactions?limit=${limit}&offset=${offset}`);
  }

  async adjustCustomerPoints(customerId, { points_delta, reason, notes = null }) {
    return this.request(`/customers/${customerId}/adjust-points`, {
      method: "POST",
      body: JSON.stringify({ points_delta, reason, notes }),
    });
  }

  // Customer CSV import (Fidelización Sprint 3)
  async previewCustomerCsvImport(restaurantId, file) {
    const url = `${this.baseUrl}/restaurants/${restaurantId}/customers/import-csv/preview`;
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(url, {
      method: "POST",
      headers: this.getAuthOnlyHeaders(),
      credentials: "include",
      body: form,
    });
    if (!response.ok) {
      let detail = "";
      try { detail = (await response.clone().json()).detail || ""; } catch { /* empty */ }
      const err = new Error(detail || `Error del servidor (${response.status})`);
      err.status = response.status;
      throw err;
    }
    return response.json();
  }

  async commitCustomerCsvImport(restaurantId, { rows, certify_consent }) {
    return this.request(`/restaurants/${restaurantId}/customers/import-csv/commit`, {
      method: "POST",
      body: JSON.stringify({ rows, certify_consent }),
    });
  }

  // Loyalty email campaigns (Fidelización Sprint 3)
  async previewLoyaltyCampaign(restaurantId, filters) {
    return this.request(`/restaurants/${restaurantId}/loyalty/campaign/preview`, {
      method: "POST",
      body: JSON.stringify({ filters }),
    });
  }

  async sendLoyaltyCampaign(restaurantId, { filters, template, subject, body, bonus_points = 0 }) {
    return this.request(`/restaurants/${restaurantId}/loyalty/campaign/send`, {
      method: "POST",
      body: JSON.stringify({ filters, template, subject, body, bonus_points }),
    });
  }

  async listLoyaltyCampaigns(restaurantId, { limit = 20 } = {}) {
    return this.request(`/restaurants/${restaurantId}/loyalty/campaigns?limit=${limit}`);
  }

  async getLoyaltyMiniDashboard(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/loyalty/mini-dashboard`);
  }

  async resendCampaignToNonOpeners(restaurantId, campaignId, { subject = null, body = null } = {}) {
    return this.request(`/restaurants/${restaurantId}/loyalty/campaign/${campaignId}/resend-non-openers`, {
      method: "POST",
      body: JSON.stringify({ subject, body }),
    });
  }

  async getLoyaltyPreferencesStats(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/loyalty/preferences-stats`);
  }

  // Loyalty Config
  async getLoyaltyConfig(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/loyalty-config`);
  }

  async saveLoyaltyConfig(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/loyalty-config`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async getLoyaltyQR(restaurantId) {
    const frontendUrl = encodeURIComponent(window.location.origin);
    return this.request(`/restaurants/${restaurantId}/loyalty-qr?frontend_url=${frontendUrl}`);
  }

  // Rewards
  async getRewards(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/rewards`);
  }

  async createReward(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/rewards`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async deleteReward(rewardId) {
    return this.request(`/rewards/${rewardId}`, { method: "DELETE" });
  }

  async updateReward(rewardId, data) {
    return this.request(`/rewards/${rewardId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async toggleReward(rewardId) {
    return this.request(`/rewards/${rewardId}/toggle`, { method: "PATCH" });
  }

  async getLoyaltyStats(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/loyalty-stats`);
  }

  async getBirthdaysToday(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/customers/birthdays-today`);
  }

  async sendBirthdayDiscount(restaurantId, customerId) {
    return this.request(`/restaurants/${restaurantId}/customers/${customerId}/birthday-discount`, { method: "POST" });
  }

  async getPublicLoyaltyQR(restaurantId) {
    const frontendUrl = encodeURIComponent(window.location.origin);
    const url = `${this.baseUrl}/public/loyalty/${restaurantId}/qr-image?frontend_url=${frontendUrl}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error("Could not load QR");
    return response.json();
  }

  async generateRewardQR(restaurantId, rewardId, customerToken) {
    const url = `${this.baseUrl}/public/loyalty/${restaurantId}/generate-reward-qr`;
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reward_id: rewardId, customer_token: customerToken }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Request failed");
    }
    return response.json();
  }

  async generateRestaurantLogo(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/generate-logo`, { method: "POST" });
  }

  // Public loyalty portal (no auth)
  async getPublicLoyaltyInfo(restaurantId) {
    const url = `${this.baseUrl}/public/loyalty/${restaurantId}`;
    const response = await fetch(url);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Request failed");
    }
    return response.json();
  }

  async registerCustomerPortal(restaurantId, data) {
    const url = `${this.baseUrl}/public/loyalty/${restaurantId}/register`;
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Request failed");
    }
    return response.json();
  }

  async googleRegisterCustomer(restaurantId, sessionId, consentGdpr = false) {
    const url = `${this.baseUrl}/public/loyalty/${restaurantId}/google-register`;
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId, consent_gdpr: consentGdpr }),
    });
    if (!response.ok) {
      const error = await response.clone().json().catch(() => ({}));
      throw new Error(error.detail || "Request failed");
    }
    return response.json();
  }

  async getCustomerPortalCard(restaurantId, customerId, token) {
    const url = `${this.baseUrl}/public/loyalty/${restaurantId}/customer/${customerId}?token=${encodeURIComponent(token)}`;
    const response = await fetch(url);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Request failed");
    }
    return response.json();
  }

  async getGoogleWalletLink(restaurantId, customerId, token) {
    const url = `${this.baseUrl}/public/loyalty/${restaurantId}/customer/${customerId}/google-wallet-link?token=${encodeURIComponent(token)}`;
    const response = await fetch(url);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Request failed");
    }
    return response.json();
  }

  async getClosingReminder(restaurantId) {
    return this.get(`/restaurants/${restaurantId}/closing-reminder`);
  }

  async updateClosingReminder(restaurantId, config) {
    return this.put(`/restaurants/${restaurantId}/closing-reminder`, config);
  }

  // Sales
  async getSales(restaurantId, days = 30) {
    return this.request(`/restaurants/${restaurantId}/sales?days=${days}`);
  }

  async createSale(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/sales`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Dashboard
  async getDashboard(restaurantId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return this.request(`/restaurants/${restaurantId}/dashboard${qs}`);
  }

  async getDaySalesDetail(restaurantId, date) {
    return this.request(`/restaurants/${restaurantId}/sales/day-detail?date=${date}`);
  }

  async getTickets(restaurantId, startDate = null, endDate = null, page = 1, pageSize = 50, search = null, searchDate = null) {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (search) params.append("search", search);
    if (searchDate) params.append("search_date", searchDate);
    return this.request(`/restaurants/${restaurantId}/tickets?${params.toString()}`);
  }

  // AI Endpoints
  async aiGenerateMenu(data, restaurantId = null) {
    const qs = restaurantId ? `?restaurant_id=${encodeURIComponent(restaurantId)}` : "";
    return this.request(`/ai/generate-menu${qs}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async aiGenerateRecipe(data, restaurantId = null, { force = false } = {}) {
    const params = new URLSearchParams();
    if (restaurantId) params.set("restaurant_id", restaurantId);
    if (force) params.set("force", "true");
    const qs = params.toString() ? `?${params.toString()}` : "";
    return this.request(`/ai/generate-recipe${qs}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async aiEstimateWaste(ingredientName, category = "", restaurantId = null) {
    const q = new URLSearchParams({ ingredient_name: ingredientName, category });
    if (restaurantId) q.set("restaurant_id", restaurantId);
    return this.request(`/ai/estimate-waste?${q.toString()}`, {
      method: "POST",
    });
  }

  async aiExtractInvoice(file, restaurantId = null) {
    const formData = new FormData();
    formData.append("file", file);

    const qs = restaurantId ? `?restaurant_id=${encodeURIComponent(restaurantId)}` : "";
    const url = `${this.baseUrl}/ai/extract-invoice${qs}`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: this.getAuthOnlyHeaders(),
      body: formData,
    });

    if (!response.ok) {
      // Try to surface the most useful error message: backend detail → text → HTTP status
      // Use clone() to avoid "body stream already read" when JSON parse fails and we fall back to text()
      let detail = null;
      try {
        const parsed = await response.clone().json();
        detail = parsed?.detail;
        if (Array.isArray(detail)) detail = detail[0]?.msg || JSON.stringify(detail);
      } catch {
        try { detail = await response.clone().text(); } catch { /* ignore */ }
      }
      const statusHint = response.status === 401 || response.status === 403
        ? "Sesión expirada. Vuelve a iniciar sesión."
        : `Error ${response.status}`;
      throw new Error(detail || statusHint || "No se pudo procesar la factura");
    }
    return response.json();
  }

  async aiDetectDocumentCorners(file) {
    const formData = new FormData();
    formData.append("file", file);
    const url = `${this.baseUrl}/ai/detect-document-corners`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: this.getAuthOnlyHeaders(),
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Document detection failed");
    }
    return response.json();
  }

  async reconcileInvoiceWithOrders(restaurantId, invoiceData) {
    return this.request(`/restaurants/${restaurantId}/shopping-lists/reconcile-invoice`, {
      method: "POST",
      body: JSON.stringify({
        supplier_name: invoiceData.supplier_name,
        supplier_id: invoiceData.supplier_id || null,
        items: invoiceData.items || [],
      }),
    });
  }

  async recordIngredientPrices(restaurantId, body) {
    return this.request(`/restaurants/${restaurantId}/ingredient-prices/record`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async getIngredientPriceHistory(restaurantId, ingredientId, days = 90) {
    return this.request(`/restaurants/${restaurantId}/ingredients/${ingredientId}/price-history?days=${days}`);
  }

  async updateRecipe(recipeId, data) {
    return this.request(`/recipes/${recipeId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async autoLinkRecipeIngredients(restaurantId, dishId, threshold = 0.75) {
    return this.request(
      `/restaurants/${restaurantId}/dishes/${dishId}/recipe/auto-link?threshold=${threshold}`,
      { method: "POST" },
    );
  }

  async aiAutofillIngredient(restaurantId, name, { create = false } = {}) {
    return this.request(`/restaurants/${restaurantId}/ingredients/ai-autofill`, {
      method: "POST",
      body: JSON.stringify({ name, create }),
    });
  }

  async suggestSuppliersForIngredient(restaurantId, ingredientName) {
    return this.request(`/restaurants/${restaurantId}/suppliers/suggest-for-ingredient`, {
      method: "POST",
      body: JSON.stringify({ ingredient_name: ingredientName }),
    });
  }

  async autoSeedSuppliers(restaurantId, { lat = null, lng = null } = {}) {
    return this.request(`/restaurants/${restaurantId}/suppliers/auto-seed`, {
      method: "POST",
      body: JSON.stringify({ lat, lng }),
    });
  }

  async getVarianceAlertConfig(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/variance-alerts/config`);
  }

  async updateVarianceAlertConfig(restaurantId, patch) {
    return this.request(`/restaurants/${restaurantId}/variance-alerts/config`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }

  async runVarianceAlertNow(restaurantId, { sendEmail = false, thresholdPct = null } = {}) {
    const params = new URLSearchParams();
    if (sendEmail) params.set("send_email", "true");
    if (thresholdPct != null) params.set("threshold_pct", String(thresholdPct));
    const qs = params.toString();
    return this.request(
      `/restaurants/${restaurantId}/variance-alerts/run-now${qs ? `?${qs}` : ""}`,
      { method: "POST" },
    );
  }

  async getVarianceAlertHistory(restaurantId, limit = 20) {
    return this.request(`/restaurants/${restaurantId}/variance-alerts/history?limit=${limit}`);
  }

  async getVarianceCauses(restaurantId, ingredientId, force = false) {
    return this.request(
      `/restaurants/${restaurantId}/variance-alerts/causes/${ingredientId}${force ? "?force=true" : ""}`,
      { method: "POST" },
    );
  }

  async searchStockImages(query, count = 5) {
    return this.request(`/images/search?query=${encodeURIComponent(query)}&count=${count}`);
  }

  async generateDishImageAI(dishName, description = "") {
    return this.request(`/ai/generate-dish-image?dish_name=${encodeURIComponent(dishName)}&description=${encodeURIComponent(description)}`, {
      method: "POST",
    });
  }

  async uploadImage(file) {
    const formData = new FormData();
    formData.append("file", file);
    
    const url = `${this.baseUrl}/images/upload`;
    
    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: this.getAuthOnlyHeaders(),
        body: formData,
      });
      
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error("Upload error:", error);
        throw new Error(error.detail || "Upload failed");
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Upload exception:", error);
      throw error;
    }
  }

  async aiForecast(restaurantId, data) {
    return this.request(`/ai/forecast?restaurant_id=${restaurantId}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getPeriodSalesSummary(restaurantId, dateFrom, dateTo) {
    return this.request(`/restaurants/${restaurantId}/sales/period-summary?date_from=${dateFrom}&date_to=${dateTo}`);
  }

  async getPeakSalesPeriod(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/sales/peak-period`);
  }

  async saveForecast(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/saved-forecasts`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getSavedForecasts(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/saved-forecasts`);
  }

  async getSavedForecast(forecastId) {
    return this.request(`/saved-forecasts/${forecastId}`);
  }

  async deleteSavedForecast(forecastId) {
    return this.request(`/saved-forecasts/${forecastId}`, { method: "DELETE" });
  }

  async aiImportMenu(data, restaurantId = null) {
    const qs = restaurantId ? `?restaurant_id=${encodeURIComponent(restaurantId)}` : "";
    return this.request(`/ai/import-menu${qs}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async aiGenerateImage(dishName, description = "") {
    return this.request(`/ai/generate-image?dish_name=${encodeURIComponent(dishName)}&description=${encodeURIComponent(description)}`, {
      method: "POST",
    });
  }

  async aiGenerateWebsiteContent(restaurantId, style = "mediterraneo") {
    return this.request(`/ai/generate-website-content?restaurant_id=${restaurantId}`, {
      method: "POST",
      body: JSON.stringify({ style }),
    });
  }

  async saveWebsiteContent(restaurantId, content) {
    return this.request(`/restaurants/${restaurantId}/website-content`, {
      method: "PUT",
      body: JSON.stringify(content),
    });
  }

  async getLogoGallery(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/logo-gallery`);
  }

  async updateLogoGallery(restaurantId, logos, activeLogo) {
    return this.request(`/restaurants/${restaurantId}/logo-gallery`, {
      method: "PUT",
      body: JSON.stringify({ logos, active_logo: activeLogo }),
    });
  }

  async getBackgroundPhotos(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/background-photos`);
  }

  async updateBackgroundPhotos(restaurantId, photos) {
    return this.request(`/restaurants/${restaurantId}/background-photos`, {
      method: "PUT",
      body: JSON.stringify({ photos }),
    });
  }

  async generateDishImage(dishId, cuisineType = "", backgroundImageUrl = null) {
    return this.request(`/dishes/${dishId}/generate-image`, {
      method: "POST",
      body: JSON.stringify({ cuisine_type: cuisineType, background_image_url: backgroundImageUrl }),
    });
  }

  // Public Website
  async getPublicRestaurant(restaurantId) {
    return this.request(`/public/restaurant/${restaurantId}`);
  }

  async toggleWebsite(restaurantId, enabled) {
    return this.request(`/restaurants/${restaurantId}/website-toggle?enabled=${enabled}`, {
      method: "PUT",
    });
  }

  // Shopping Lists
  async getShoppingLists(restaurantId, status = null) {
    const params = status ? `?status=${status}` : "";
    return this.request(`/restaurants/${restaurantId}/shopping-lists${params}`);
  }

  async createShoppingList(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/shopping-lists`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateShoppingListStatus(listId, status, updateStock = false) {
    return this.request(`/shopping-lists/${listId}/status?status=${status}&update_stock=${updateStock}`, {
      method: "PUT",
    });
  }

  async updateShoppingListItems(listId, items) {
    return this.request(`/shopping-lists/${listId}/items`, {
      method: "PUT",
      body: JSON.stringify({ items }),
    });
  }

  async sendShoppingListEmail(listId, recipientEmail, organizedData = null) {
    return this.request(`/shopping-lists/${listId}/send-email`, {
      method: "POST",
      body: JSON.stringify({
        recipient_email: recipientEmail,
        organized_data: organizedData || null,
      }),
    });
  }

  async downloadShoppingListPdf(listId, organizedData) {
    const response = await fetch(`${this.baseUrl}/shopping-lists/${listId}/pdf`, {
      method: "POST",
      headers: this.getHeaders(),
      credentials: "include",
      body: JSON.stringify({ organized_data: organizedData }),
    });
    if (response.status === 401) {
      this.clearToken();
      window.location.href = "/login";
      throw new Error("Sesión expirada");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Error al generar el PDF");
    }
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : "lista_compra.pdf";
    return { blob, filename };
  }

  async deleteShoppingList(listId) {
    return this.request(`/shopping-lists/${listId}`, {
      method: "DELETE",
    });
  }

  async convertListToOrder(listId, supplierId) {
    return this.request(`/shopping-lists/${listId}/convert-to-order?supplier_id=${supplierId}`, {
      method: "POST",
    });
  }

  // AI generate ingredient image
  async generateIngredientImage(name, restaurantId = null) {
    const q = new URLSearchParams({ name });
    if (restaurantId) q.set("restaurant_id", restaurantId);
    return this.request(`/ingredients/generate-image?${q.toString()}`, {
      method: "POST",
    });
  }

  async generateIngredientImageById(ingredientId) {
    return this.request(`/ingredients/${ingredientId}/generate-image`, {
      method: "POST",
    });
  }

  // Supermarkets
  async getNearbySupermarkets(lat, lon, radius = 2000) {
    return this.request(`/supermarkets/nearby?lat=${lat}&lon=${lon}&radius=${radius}`);
  }

  // AI organize shopping list by aisles
  async organizeShoppingListByAisles(listId, supermarketName = "", supermarketBrandKey = "") {
    const params = new URLSearchParams({ supermarket_name: supermarketName, supermarket_brand_key: supermarketBrandKey });
    return this.request(`/shopping-lists/${listId}/organize-by-aisles?${params}`, {
      method: "POST",
    });
  }

  // Demo Data
  async generateDemoData(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/demo/generate`, {
      method: "POST",
    });
  }

  async clearDemoData(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/demo/clear`, {
      method: "DELETE",
    });
  }

  async getDemoStatus(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/demo/status`);
  }

  // Public restaurant page by slug (no auth needed)
  async getPublicRestaurantBySlug(slug) {
    const url = `${this.baseUrl}/public/r/${slug}`;
    const response = await fetch(url);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Restaurant not found");
    }
    return response.json();
  }

  // Sales Summary for History-based Forecast
  async getSalesSummary(restaurantId, days = 30) {
    return this.request(`/restaurants/${restaurantId}/sales/summary?days=${days}`);
  }

  // TPV Integration
  async getTPVStatus(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/tpv/status`);
  }

  async getTPVApiKeys(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/tpv/api-keys`);
  }

  async createTPVApiKey(restaurantId, name, permissions) {
    return this.request(`/restaurants/${restaurantId}/tpv/api-keys?name=${encodeURIComponent(name)}&permissions=${encodeURIComponent(permissions)}`, {
      method: "POST",
    });
  }

  async deleteTPVApiKey(restaurantId, keyId) {
    return this.request(`/restaurants/${restaurantId}/tpv/api-keys/${keyId}`, {
      method: "DELETE",
    });
  }

  async getTPVDocs() {
    return this.request("/tpv/docs");
  }

  async importSalesCSV(restaurantId, file, preview = false, columnMapping = null) {
    const formData = new FormData();
    formData.append("file", file);
    if (columnMapping && !preview) {
      formData.append("column_mapping_json", JSON.stringify(columnMapping));
    }
    const url = `${this.baseUrl}/restaurants/${restaurantId}/import/sales-csv${preview ? "?preview=true" : ""}`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: this.getAuthOnlyHeaders(),
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Import failed");
    }
    return response.json();
  }

  async importMenuCSV(restaurantId, file, preview = false, columnMapping = null) {
    const formData = new FormData();
    formData.append("file", file);
    if (columnMapping && !preview) {
      formData.append("column_mapping_json", JSON.stringify(columnMapping));
    }
    const url = `${this.baseUrl}/restaurants/${restaurantId}/import/menu-csv${preview ? "?preview=true" : ""}`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: this.getAuthOnlyHeaders(),
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Import failed");
    }
    return response.json();
  }

  async importInventoryCSV(restaurantId, file, preview = false, columnMapping = null) {
    const formData = new FormData();
    formData.append("file", file);
    if (columnMapping && !preview) {
      formData.append("column_mapping_json", JSON.stringify(columnMapping));
    }
    const url = `${this.baseUrl}/restaurants/${restaurantId}/import/inventory-csv${preview ? "?preview=true" : ""}`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: this.getAuthOnlyHeaders(),
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Import failed");
    }
    return response.json();
  }

  async importExpensesCSV(restaurantId, file, preview = false, columnMapping = null) {
    const formData = new FormData();
    formData.append("file", file);
    if (columnMapping && !preview) {
      formData.append("column_mapping_json", JSON.stringify(columnMapping));
    }
    const url = `${this.baseUrl}/restaurants/${restaurantId}/import/expenses-csv${preview ? "?preview=true" : ""}`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: this.getAuthOnlyHeaders(),
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Import failed");
    }
    return response.json();
  }

  // POS Intelligent Integration
  async getPOSSystems(countryCode = null) {
    const qs = countryCode ? `?country_code=${countryCode}` : "";
    return this.request(`/pos/systems${qs}`);
  }

  async getPOSConfig(restaurantId) {
    return this.request(`/pos/config/${restaurantId}`);
  }

  async savePOSConfig(restaurantId, data) {
    return this.request(`/pos/config/${restaurantId}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async aiPOSRecommend(posName, restaurantType = "restaurante") {
    return this.request("/pos/ai-recommend", {
      method: "POST",
      body: JSON.stringify({ pos_name: posName, restaurant_type: restaurantType }),
    });
  }

  async schedulePOSReminder(restaurantId, email, frequency, enabled) {
    return this.request(`/pos/schedule-reminder/${restaurantId}`, {
      method: "POST",
      body: JSON.stringify({ email, frequency, enabled }),
    });
  }

  // TPV Live Feed
  async getTPVLiveFeed(restaurantId, limit = 30) {
    return this.request(`/restaurants/${restaurantId}/tpv/live-feed?limit=${limit}`);
  }

  async sendTPVTestEvent(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/tpv/test-event`, { method: "POST" });
  }

  // ── Imported Dishes ───────────────────────────────────────────────────────
  async getImportedDishes(restaurantId, search = "", status = "pending") {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    return this.request(`/restaurants/${restaurantId}/imported-dishes?${params.toString()}`);
  }

  async incorporateImportedDishes(restaurantId, dishIds) {
    return this.request(`/restaurants/${restaurantId}/imported-dishes/incorporate`, {
      method: "POST",
      body: JSON.stringify({ dish_ids: dishIds }),
    });
  }

  async dismissImportedDish(restaurantId, dishId) {
    return this.request(`/restaurants/${restaurantId}/imported-dishes/${dishId}`, {
      method: "DELETE",
    });
  }

  // ── Notifications ─────────────────────────────────────────────────────────
  async getNotifications() {
    return this.request("/notifications");
  }

  async markNotificationRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, { method: "PATCH" });
  }

  async markAllNotificationsRead() {
    return this.request("/notifications/read-all", { method: "PATCH" });
  }

  async deleteNotification(notificationId) {
    return this.request(`/notifications/${notificationId}`, { method: "DELETE" });
  }

  async deleteAllNotifications() {
    return this.request("/notifications/all", { method: "DELETE" });
  }

  async getPushDevices() {
    return this.request("/notifications/push/devices");
  }

  async revokePushDevice(tokenId) {
    return this.request(`/notifications/push/devices/${tokenId}/revoke`, {
      method: "POST",
    });
  }

  async pushBroadcast(restaurantId, { title, message, scope = "team" }) {
    return this.request(`/restaurants/${restaurantId}/push-broadcast`, {
      method: "POST",
      body: JSON.stringify({ title, message, scope }),
    });
  }

  async getPushBroadcastHistory(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/push-broadcast/history`);
  }

  // ── Background AI generation (batch) ─────────────────────────────────────
  async generateImagesBatch(restaurantId, dishIds, cuisineType = "", backgroundImageUrls = []) {
    return this.request(`/restaurants/${restaurantId}/ai/generate-images-batch`, {
      method: "POST",
      body: JSON.stringify({
        dish_ids: dishIds,
        cuisine_type: cuisineType,
        background_image_urls: backgroundImageUrls,
      }),
    });
  }

  async generateRecipesBatch(restaurantId, dishIds) {
    return this.request(`/restaurants/${restaurantId}/ai/generate-recipes-batch`, {
      method: "POST",
      body: JSON.stringify({ dish_ids: dishIds }),
    });
  }

  async generateRecipesProfessionalBulk(restaurantId, dishIds) {
    return this.request(`/restaurants/${restaurantId}/ai/generate-recipes-professional-bulk`, {
      method: "POST",
      body: JSON.stringify({ dish_ids: dishIds }),
    });
  }

  async getBatchStatus(restaurantId, batchId) {
    return this.request(`/restaurants/${restaurantId}/ai/batch-status/${batchId}`);
  }

  async simulateMenuCost(menuId, dishIds) {
    return this.request(`/menus/${menuId}/cost-simulator`, {
      method: "POST",
      body: JSON.stringify({ dish_ids: dishIds }),
    });
  }

  async getCostExtremes(menuId) {
    return this.request(`/menus/${menuId}/cost-extremes`);
  }

  async getAvailableDishes(menuId) {
    return this.request(`/menus/${menuId}/available-dishes`);
  }

  async addDailyMenuItems(menuId, dishIds, categoryId) {
    return this.request(`/menus/${menuId}/daily-items`, {
      method: "POST",
      body: JSON.stringify({ dish_ids: dishIds, category_id: categoryId }),
    });
  }

  async removeDailyMenuItem(menuId, dishId) {
    return this.request(`/menus/${menuId}/daily-items/${dishId}`, { method: "DELETE" });
  }

  async getDailyMenuItems(menuId) {
    return this.request(`/menus/${menuId}/daily-items`);
  }

  async getArchivedMenus(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/menus/archived`);
  }

  async deleteMenuPermanent(menuId) {
    return this.request(`/menus/${menuId}/permanent`, { method: "DELETE" });
  }

  // ── Stripe Payments ─────────────────────────────────────────────────────
  async createSubscriptionCheckout(planId, restaurantId, billingPeriod = "monthly") {
    return this.request("/stripe/checkout/subscription", {
      method: "POST",
      body: JSON.stringify({
        plan_id: planId,
        restaurant_id: restaurantId,
        billing_period: billingPeriod,
        origin_url: window.location.origin,
      }),
    });
  }

  async createTopUpCheckout(packId, restaurantId) {
    return this.request("/stripe/checkout/top-up", {
      method: "POST",
      body: JSON.stringify({
        pack_id: packId,
        restaurant_id: restaurantId,
        origin_url: window.location.origin,
      }),
    });
  }

  async getCheckoutStatus(sessionId) {
    return this.request(`/stripe/checkout/status/${sessionId}`);
  }

  // ─── Auto-refill de créditos IA (cobro off-session) ──────────
  async getAutoRefill(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/auto-refill`);
  }
  async updateAutoRefill(restaurantId, patch) {
    return this.request(`/restaurants/${restaurantId}/auto-refill`, {
      method: "PUT", body: JSON.stringify(patch),
    });
  }
  async createAutoRefillSetup(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/auto-refill/setup-session`, {
      method: "POST", body: JSON.stringify({ origin_url: window.location.origin }),
    });
  }
  async completeAutoRefillSetup(restaurantId, sessionId) {
    return this.request(`/restaurants/${restaurantId}/auto-refill/setup-complete`, {
      method: "POST", body: JSON.stringify({ session_id: sessionId }),
    });
  }
  async removeAutoRefillCard(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/auto-refill/card`, { method: "DELETE" });
  }

  async getPaymentHistory(restaurantId) {
    return this.request(`/stripe/payments/${restaurantId}`);
  }

  async generateHeroImage(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/ai/generate-hero-image`, {
      method: "POST",
    });
  }

  // ── Allergens ───────────────────────────────────────────────────────────
  async getGlobalAllergens() {
    return this.request("/global/allergens");
  }

  async getDishAllergens(restaurantId, dishId) {
    return this.request(`/restaurants/${restaurantId}/dishes/${dishId}/allergens`);
  }

  async recomputeDishAllergens(restaurantId, dishId) {
    return this.request(`/restaurants/${restaurantId}/dishes/${dishId}/recompute-allergens`, { method: "POST" });
  }

  async verifyDishAllergens(restaurantId, dishId, data) {
    return this.request(`/restaurants/${restaurantId}/dishes/${dishId}/verify-allergens`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async recomputeAllAllergens(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/recompute-all-allergens`, { method: "POST" });
  }

  async getDishesAllergenStatus(restaurantId, search = "") {
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    return this.request(`/restaurants/${restaurantId}/dishes-allergen-status${params}`);
  }

  async aiInferDishAllergens(restaurantId, dishId) {
    return this.request(`/restaurants/${restaurantId}/dishes/${dishId}/ai-infer-allergens`, { method: "POST" });
  }

  async aiInferAllergensBatch(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/ai-infer-allergens-batch`, { method: "POST" });
  }

  async getAllergenReviews(restaurantId, status = "pending") {
    return this.request(`/restaurants/${restaurantId}/allergen-reviews?status=${status}`);
  }

  async approveAllergenReview(restaurantId, dishId) {
    return this.request(`/restaurants/${restaurantId}/allergen-reviews/${dishId}/approve`, { method: "POST" });
  }

  async rejectAllergenReview(restaurantId, dishId) {
    return this.request(`/restaurants/${restaurantId}/allergen-reviews/${dishId}/reject`, { method: "POST" });
  }

  async bulkApproveHighConfidenceAllergens(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/allergens/bulk-approve-high-confidence`, { method: "POST" });
  }

  async importAllergens(restaurantId, data) {
    return this.request(`/restaurants/${restaurantId}/allergen-imports`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getAllergensComplianceSummary(restaurantId) {
    return this.request(`/restaurants/${restaurantId}/allergens/compliance`);
  }

  async downloadAllergensComplianceReport(restaurantId) {
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const filename = `compliance_alergenos_${restaurantId}_${today}.pdf`;
    return this.downloadFile(
      `/restaurants/${restaurantId}/allergens/compliance.pdf`,
      filename,
    );
  }
}

export const api = new ApiClient();
export default api;

/**
 * Drop-in replacement for fetch() that injects Bearer token.
 * Use this for components that need direct fetch with auth (file uploads, etc.)
 */
export function apiFetch(url, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (api._token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${api._token}`;
  }
  return fetch(url, { ...options, headers, credentials: "include" });
}
